import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { TabGroup } from '@/components/common/TabGroup';
import CallsTab from '@/components/contacts/CallsTab';
import { ChatCore, ContactChatHeader } from '@/features/chat';
import { getFullPhoneE164 } from '@/lib/utils';
import {
  ArrowLeft,
  Calendar,
  Video,
  Phone,
  User,
  ExternalLink,
  Play,
  Save,
  CheckCircle2,
  Clock,
  Mail,
  Package,
  MoreVertical,
  ChevronDown,
  FileText,
  Link,
  ThumbsUp,
  AlertCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import OnboardingChecklistItem, { ChecklistItemStatus } from '@/components/onboarding/OnboardingChecklistItem';
import OnboardingPeopleReview from '@/components/onboarding/OnboardingPeopleReview';
import OnboardingEligibilityReview from '@/components/onboarding/OnboardingEligibilityReview';
import { useOnboardingSessions, OnboardingChecklistItem as ChecklistItemType } from '@/hooks/useOnboardingSessions';
import { useContracts } from '@/hooks/useContracts';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { formatPhoneNumber } from '@/lib/utils';

interface ExtendedChecklistItem extends ChecklistItemType {
  script?: string;
  status: ChecklistItemStatus;
  required: boolean;
}

export default function OnboardingDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const itemId = searchParams.get('id');

  const [activeTab, setActiveTab] = useState('checklist');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [checklist, setChecklist] = useState<ExtendedChecklistItem[]>([]);

  // Fetch item details
  const { data: item, isLoading: itemLoading } = useQuery({
    queryKey: ['onboarding-item', itemId],
    queryFn: async () => {
      if (!itemId) return null;
      const { data, error } = await supabase
        .from('items')
        .select(`
          id,
          status,
          catalog_id,
          deal_id,
          eligibility,
          catalog:catalog_id (
            id,
            name,
            type,
            onboarding_checklist
          ),
          deal:deal_id (
            id,
            name,
            contact_id,
            sales_consultant,
            contact:contact_id (
              id,
              first_name,
              last_name,
              primary_phone,
              primary_phone_country_code,
              primary_email
            ),
            sales_consultant_user:sales_consultant (
              id,
              display_name
            )
          )
        `)
        .eq('id', itemId)
        .single();

      if (error) throw error;
      return data as unknown as {
        id: string;
        status: string;
        catalog_id: string;
        deal_id: string;
        eligibility: Record<string, any> | null;
        catalog: {
          id: string;
          name: string;
          type: string;
          onboarding_checklist: unknown;
        } | null;
        deal: {
          id: string;
          name: string;
          contact_id: string;
          sales_consultant: string;
          contact: {
            id: string;
            first_name: string | null;
            last_name: string | null;
            primary_phone: string | null;
            primary_phone_country_code: string | null;
            primary_email: string | null;
          } | null;
          sales_consultant_user: {
            id: string;
            display_name: string;
          } | null;
        } | null;
      };
    },
    enabled: !!itemId,
  });

  const catalogId = item?.catalog_id;
  const dealId = item?.deal_id;

  const {
    sessions,
    isLoading: sessionsLoading,
    activeSession,
    updateSession,
    startSession,
    completeSession,
    approveAndFinalize,
    isUpdating,
  } = useOnboardingSessions(itemId || undefined, catalogId || undefined);

  // Fetch contracts for the deal
  const { contracts, loading: contractsLoading } = useContracts(dealId || undefined);

  // Initialize checklist from active session or catalog
  useEffect(() => {
    if (activeSession?.checklist) {
      const catalogChecklist = item?.catalog?.onboarding_checklist as unknown as Array<{
        key: string;
        label: string;
        script?: string;
        required?: boolean;
      }> | null;

      const extendedChecklist: ExtendedChecklistItem[] = activeSession.checklist.map((sessionItem) => {
        const catalogItem = catalogChecklist?.find((c) => c.key === sessionItem.key);
        
        let status: ChecklistItemStatus = 'pending';
        if (sessionItem.completed) {
          status = 'confirmed';
        } else if (sessionItem.notes) {
          status = 'not_confirmed';
        }

        return {
          ...sessionItem,
          script: catalogItem?.script || '',
          status,
          required: catalogItem?.required ?? true,
        };
      });

      setChecklist(extendedChecklist);
      setMeetingNotes(activeSession.meeting_notes || '');
      setRecordingUrl(activeSession.recording_url || '');
    }
  }, [activeSession, item]);

  const handleChecklistUpdate = (index: number, status: ChecklistItemStatus, notes?: string) => {
    const updated = [...checklist];
    updated[index] = {
      ...updated[index],
      status,
      completed: status === 'confirmed',
      notes: notes || updated[index].notes,
    };
    setChecklist(updated);
  };

  const handleConfirm = (index: number) => {
    handleChecklistUpdate(index, 'confirmed');
  };

  const handleReject = (index: number, notes: string) => {
    handleChecklistUpdate(index, 'not_confirmed', notes);
  };

  const handleReset = (index: number) => {
    handleChecklistUpdate(index, 'pending');
  };

  const handleSaveProgress = async () => {
    if (!activeSession) return;

    try {
      const sessionChecklist = checklist.map((item) => ({
        key: item.key,
        label: item.label,
        completed: item.completed,
        notes: item.notes,
      }));

      await updateSession({
        id: activeSession.id,
        checklist: sessionChecklist,
        meeting_notes: meetingNotes,
        recording_url: recordingUrl,
      });

      toast.success('Progresso salvo');
    } catch (error) {
      console.error(error);
    }
  };

  const handleStartSession = async () => {
    if (!activeSession || activeSession.status !== 'scheduled') return;
    await startSession(activeSession.id);
  };

  const handleCompleteSession = async () => {
    if (!activeSession) return;

    const requiredItems = checklist.filter((item) => item.required);
    const allRequiredHandled = requiredItems.every(
      (item) => item.status === 'confirmed' || item.status === 'not_confirmed'
    );

    if (!allRequiredHandled) {
      toast.error('Todos os itens obrigatórios devem ser validados');
      return;
    }

    try {
      const sessionChecklist = checklist.map((item) => ({
        key: item.key,
        label: item.label,
        completed: item.status === 'confirmed',
        notes: item.notes,
      }));

      await completeSession({
        sessionId: activeSession.id,
        checklist: sessionChecklist,
        meeting_notes: meetingNotes,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleApprove = async () => {
    const completedSession = sessions.find((s) => s.status === 'completed' && !s.client_approved);
    if (!completedSession) return;

    await approveAndFinalize(completedSession.id);
  };

  const contact = item?.deal?.contact;
  const getContactName = () => {
    if (!contact) return 'Cliente não identificado';
    return [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Cliente';
  };

  const getContactInitial = () => {
    if (!contact) return 'C';
    return contact.first_name?.charAt(0).toUpperCase() || 'C';
  };

  const getStatusBadge = () => {
    if (!activeSession) {
      return <Badge variant="outline">Sem sessão</Badge>;
    }

    switch (activeSession.status) {
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-0">Agendado</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 border-0">Em andamento</Badge>;
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 border-0">Concluído</Badge>;
      default:
        return <Badge variant="outline">{activeSession.status}</Badge>;
    }
  };

  const getCompletionStats = () => {
    const total = checklist.length;
    const confirmed = checklist.filter((i) => i.status === 'confirmed').length;
    const notConfirmed = checklist.filter((i) => i.status === 'not_confirmed').length;
    const handled = confirmed + notConfirmed;
    return { total, confirmed, notConfirmed, handled };
  };

  const goToContactDetail = () => {
    if (contact?.id) {
      navigate(`/contacts/${contact.id}`);
    }
  };

  if (!itemId) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6">
        <p className="text-muted-foreground">Item não encontrado</p>
      </div>
    );
  }

  if (itemLoading || sessionsLoading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-3 sm:p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const stats = getCompletionStats();
  const canComplete = activeSession?.status === 'in_progress' && stats.handled === stats.total;
  const completedSession = sessions.find((s) => s.status === 'completed' && !s.client_approved);

  const tabs = [
    { id: 'checklist', label: 'Checklist' },
    { id: 'notes', label: 'Notas' },
    { id: 'whatsapp', label: 'Mensagens' },
    { id: 'calls', label: 'Ligações' },
    { id: 'history', label: 'Histórico' },
  ];

  return (
    <div className="bg-white dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button 
          onClick={() => navigate('/onboarding')} 
          variant="ghost" 
          size="sm"
          className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Onboarding
        </Button>

        {/* Contact Card Header */}
        <div className="mb-4 md:mb-6">
          {contact ? (
            <Card className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
              <CardContent className="p-3 sm:p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 md:gap-4">
                  {/* Contact Info */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-6 flex-1 min-w-0">
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg md:text-2xl">
                          {getContactInitial()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <button 
                          onClick={goToContactDetail} 
                          className="text-base md:text-xl font-bold text-slate-900 dark:text-slate-100 hover:text-blue-600 hover:underline text-left truncate max-w-full block"
                        >
                          {getContactName()}
                        </button>
                        {item?.deal?.sales_consultant_user && (
                          <div className="flex items-center gap-2 mt-1 text-xs md:text-sm text-slate-500 dark:text-slate-400">
                            <User className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                            <span className="truncate">Consultor: {item.deal.sales_consultant_user.display_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Email and Phone */}
                    <div className="hidden sm:flex flex-col gap-2 text-xs md:text-sm text-slate-600 dark:text-slate-400 min-w-0">
                      {contact.primary_email && (
                        <div className="flex items-center gap-2 min-w-0">
                          <Mail className="w-3 h-3 md:w-4 md:h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                          <a href={`mailto:${contact.primary_email}`} className="hover:text-blue-600 transition-colors truncate">
                            {contact.primary_email}
                          </a>
                        </div>
                      )}
                      {contact.primary_phone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const callEvent = new CustomEvent('initiate-call', {
                              detail: {
                                contactId: contact.id,
                                phoneNumber: contact.primary_phone,
                                contactName: getContactName()
                              }
                            });
                            window.dispatchEvent(callEvent);
                          }}
                          className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer group"
                        >
                          <Phone className="w-3 h-3 md:w-4 md:h-4 text-slate-400 dark:text-slate-500 group-hover:text-blue-600 flex-shrink-0" />
                          <span className="hover:underline">
                            {formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                      Cliente não identificado
                    </h1>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Item/Product Info Header */}
        <Card className="mb-4 md:mb-6 border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 shadow-sm">
          <CardContent className="py-2 px-3 sm:px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              {/* Product Name and Badge */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <h2 className="font-semibold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate flex-1 min-w-0">
                  {item?.catalog?.name || 'Produto'}
                </h2>
                {getStatusBadge()}
              </div>
              
              {/* Session Info and Actions */}
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                {activeSession?.scheduled_at && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">
                      {safeFormat(new Date(activeSession.scheduled_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                    </span>
                    <span className="sm:hidden">
                      {safeFormat(new Date(activeSession.scheduled_at), "dd/MM HH:mm")}
                    </span>
                  </div>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 sm:gap-2 h-8 px-2 sm:px-3">
                      <span className="hidden sm:inline">Ações</span>
                      <MoreVertical className="w-4 h-4 sm:hidden" />
                      <ChevronDown className="w-4 h-4 hidden sm:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Gestão de Sessão */}
                    {activeSession?.status === 'scheduled' && (
                      <>
                        <DropdownMenuItem onClick={handleStartSession}>
                          <Play className="w-4 h-4 mr-2" />
                          Iniciar Sessão
                        </DropdownMenuItem>
                        
                        {activeSession?.meeting_url && activeSession.channel === 'video' && (
                          <DropdownMenuItem asChild>
                            <a href={activeSession.meeting_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Abrir Reunião
                            </a>
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    
                    {activeSession?.status === 'in_progress' && canComplete && (
                      <DropdownMenuItem onClick={handleCompleteSession}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Completar Sessão
                      </DropdownMenuItem>
                    )}
                    
                    {completedSession && (
                      <DropdownMenuItem onClick={handleApprove}>
                        <ThumbsUp className="w-4 h-4 mr-2" />
                        Aprovar Onboarding
                      </DropdownMenuItem>
                    )}
                    
                    {/* Navegação */}
                    {item?.deal_id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate(`/deals/detail?id=${item.deal_id}`)}>
                          <Link className="w-4 h-4 mr-2" />
                          Ir para Negociação
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Session Action Bar */}
        {activeSession?.status === 'scheduled' && (
          <Card className="mb-4 md:mb-6 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  {activeSession.channel === 'voice' ? (
                    <Phone className="h-5 w-5" />
                  ) : (
                    <Video className="h-5 w-5" />
                  )}
                  <span className="font-medium">
                    {activeSession.channel === 'voice' ? 'Ligação' : 'Vídeo chamada'} agendada
                  </span>
                </div>
              </div>
              <Button onClick={handleStartSession} className="bg-blue-600 hover:bg-blue-700">
                <Play className="h-4 w-4 mr-2" />
                Iniciar Sessão
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <TabGroup 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
          className="mb-6" 
        />

        {/* Tab Content */}
        {activeTab === 'checklist' && (
          <div className="space-y-6">
            {/* Checklist Stats */}
            {checklist.length > 0 && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">Progresso:</span>
                <span className="text-emerald-600 font-medium">{stats.confirmed} confirmados</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-destructive font-medium">{stats.notConfirmed} não confirmados</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{stats.total - stats.handled} pendentes</span>
              </div>
            )}

            {/* Checklist Items */}
            {checklist.length > 0 ? (
              <div className="space-y-3">
                {checklist.map((checklistItem, index) => {
                  // Render contract preview for contract_review item
                  const renderContractPreview = () => {
                    if (checklistItem.key !== 'contract_review') return undefined;
                    
                    if (contractsLoading) {
                      return (
                        <div className="p-3 rounded-lg bg-muted/50 border border-border">
                          <Skeleton className="h-10 w-full" />
                        </div>
                      );
                    }
                    
                    if (!contracts || contracts.length === 0) {
                      return (
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <p className="text-sm text-amber-700 dark:text-amber-400">
                              Nenhum contrato encontrado para este deal.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-2">
                        {contracts.map((contract) => {
                          const getContractStatusBadge = () => {
                            switch (contract.status) {
                              case 'completed':
                                return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 border-0 text-xs">Assinado</Badge>;
                              case 'sent':
                                return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-0 text-xs">Enviado</Badge>;
                              case 'viewed':
                                return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 border-0 text-xs">Visualizado</Badge>;
                              case 'draft':
                                return <Badge variant="outline" className="text-xs">Rascunho</Badge>;
                              case 'declined':
                                return <Badge variant="destructive" className="text-xs">Recusado</Badge>;
                              case 'expired':
                                return <Badge variant="secondary" className="text-xs">Expirado</Badge>;
                              default:
                                return <Badge variant="outline" className="text-xs">{contract.status}</Badge>;
                            }
                          };
                          
                          return (
                            <div 
                              key={contract.id} 
                              className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <div>
                                  <span className="text-sm font-medium text-foreground">
                                    {contract.contract_name}
                                  </span>
                                  <div className="flex items-center gap-2 mt-1">
                                    {getContractStatusBadge()}
                                    <span className="text-xs text-muted-foreground">
                                      {contract.contract_type}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {(contract.contract_url || contract.pandadoc_view_url || contract.signed_file_url) && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      const url = contract.signed_file_url || contract.pandadoc_view_url || contract.contract_url;
                                      if (url) window.open(url, '_blank');
                                    }}
                                    className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    Visualizar
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  };

                  // Render people review for people_review item
                  const renderPeopleReview = () => {
                    if (checklistItem.key !== 'people_review') return undefined;
                    return (
                      <OnboardingPeopleReview
                        itemId={itemId!}
                        catalogId={catalogId!}
                        readonly={activeSession?.status !== 'in_progress'}
                      />
                    );
                  };

                  // Render eligibility review for eligibility_review item
                  const renderEligibilityReview = () => {
                    if (checklistItem.key !== 'eligibility_review') return undefined;
                    return (
                      <OnboardingEligibilityReview
                        itemId={itemId!}
                        catalogId={catalogId!}
                        currentEligibility={item?.eligibility || undefined}
                        readonly={activeSession?.status !== 'in_progress'}
                      />
                    );
                  };

                  // Get extra content for the current item
                  const getExtraContent = () => {
                    return renderContractPreview() || renderPeopleReview() || renderEligibilityReview();
                  };
                  
                  return (
                    <OnboardingChecklistItem
                      key={checklistItem.key}
                      index={index}
                      label={checklistItem.label}
                      script={checklistItem.script}
                      status={checklistItem.status}
                      notes={checklistItem.notes}
                      required={checklistItem.required}
                      onConfirm={() => handleConfirm(index)}
                      onReject={(notes) => handleReject(index, notes)}
                      onReset={() => handleReset(index)}
                      extraContent={getExtraContent()}
                    />
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum checklist disponível</h3>
                  <p className="text-muted-foreground">
                    O checklist será carregado quando uma sessão estiver ativa.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            {activeSession && activeSession.status === 'in_progress' && (
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleSaveProgress} disabled={isUpdating}>
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Progresso
                </Button>
                <Button onClick={handleCompleteSession} disabled={!canComplete || isUpdating}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Finalizar Sessão
                </Button>
              </div>
            )}

            {/* Approve Button */}
            {completedSession && (
              <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30">
                <CardContent className="p-6 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Sessão Concluída</h3>
                  <p className="text-muted-foreground mb-4">
                    O cliente confirmou todos os itens. Aprove para criar o caso/processo.
                  </p>
                  <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700">
                    Aprovar e Criar Caso
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-6">
            {activeSession && (activeSession.status === 'in_progress' || activeSession.status === 'completed') ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Notas e Gravação</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Notas da Sessão</Label>
                    <Textarea
                      placeholder="Anotações importantes sobre a sessão..."
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value)}
                      className="min-h-[200px]"
                      disabled={activeSession.status === 'completed'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link da Gravação</Label>
                    <Input
                      placeholder="https://..."
                      value={recordingUrl}
                      onChange={(e) => setRecordingUrl(e.target.value)}
                      disabled={activeSession.status === 'completed'}
                    />
                  </div>
                  {activeSession.status === 'in_progress' && (
                    <div className="flex justify-end pt-4">
                      <Button variant="outline" onClick={handleSaveProgress} disabled={isUpdating}>
                        <Save className="h-4 w-4 mr-2" />
                        Salvar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Sessão não iniciada</h3>
                  <p className="text-muted-foreground">
                    As notas estarão disponíveis quando a sessão for iniciada.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'whatsapp' && contact && (
          <ChatCore
            contactId={contact.id}
            contactPhone={getFullPhoneE164(contact.primary_phone || '', contact.primary_phone_country_code || undefined)}
            showConversationSelector
            header={
              <ContactChatHeader
                phoneNumber={getFullPhoneE164(contact.primary_phone || '', contact.primary_phone_country_code || undefined)}
                isConnected
              />
            }
          />
        )}

        {activeTab === 'calls' && (
          <CallsTab 
            contactId={contact?.id || null}
            dealId={item?.deal_id || null}
          />
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {sessions.length > 0 ? (
              sessions.map((session) => (
                <Card key={session.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          session.status === 'completed' ? 'bg-emerald-500' :
                          session.status === 'in_progress' ? 'bg-amber-500' :
                          'bg-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-foreground">
                            Sessão {session.status === 'completed' ? 'concluída' : 
                                    session.status === 'in_progress' ? 'em andamento' : 'agendada'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {session.scheduled_at 
                              ? safeFormat(new Date(session.scheduled_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
                              : 'Data não definida'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {session.channel === 'voice' ? 'Ligação' : 'Vídeo'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma sessão</h3>
                  <p className="text-muted-foreground">
                    Não há sessões registradas para este onboarding.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* No Session Message */}
        {!activeSession && sessions.length === 0 && activeTab === 'checklist' && (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma sessão agendada</h3>
              <p className="text-muted-foreground mb-4">
                Volte para a tela de onboarding para agendar uma sessão.
              </p>
              <Button variant="outline" onClick={() => navigate('/onboarding')}>
                Voltar para Onboarding
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
